import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaUsers, FaSignOutAlt } from 'react-icons/fa';

const AdminProfileMenu = () => {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [adminUser, setAdminUser] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState('/images/admin profile photo.jpg');
  const [imageError, setImageError] = useState(false);
  const navigate = useNavigate();

  // Load admin user data from localStorage
  useEffect(() => {
    const loadAdminData = () => {
      try {
        const adminUserData = localStorage.getItem('collegeAdminUser');
        if (adminUserData && adminUserData !== 'undefined') {
          const parsedUser = JSON.parse(adminUserData);
          setAdminUser(parsedUser);
          
          // Set profile image URL
          if (parsedUser.profilePhoto) {
            const imageUrl = `/api/admin/profile-photo/${parsedUser.profilePhoto}?t=${Date.now()}`;
            setProfileImageUrl(imageUrl);
            setImageError(false);
          } else {
            setProfileImageUrl('/images/admin profile photo.jpg');
            setImageError(false);
          }
        }
      } catch (err) {
        console.error('Error loading admin user data in ProfileMenu:', err);
        setProfileImageUrl('/images/admin profile photo.jpg');
      }
    };

    // Load initially
    loadAdminData();

    // Listen for localStorage changes (when profile is updated in another component)
    const handleStorageChange = (e) => {
      if (e.key === 'collegeAdminUser') {
        loadAdminData();
      }
    };

    // Listen for storage events from other tabs/windows
    window.addEventListener('storage', handleStorageChange);

    // Listen for custom events from the same tab (like when MyAccount2 updates)
    const handleCustomStorageUpdate = () => {
      loadAdminData();
    };
    window.addEventListener('adminProfileUpdated', handleCustomStorageUpdate);

    // Cleanup
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('adminProfileUpdated', handleCustomStorageUpdate);
    };
  }, []);

  const handleImageError = () => {
    if (!imageError) {
      console.warn('Failed to load admin profile photo, falling back to default');
      setImageError(true);
      setProfileImageUrl('/images/admin profile photo.jpg');
    }
  };

  const handleLogout = () => {
    try {
      localStorage.removeItem('collegeAdminToken');
      localStorage.removeItem('collegeAdminUser');
      localStorage.removeItem('collegeAdminRole');
      navigate('/admin-login');
      setOpen(false);
    } catch (err) {
      setError('Failed to log out. Please try again.');
    }
  };

  const handleNavigation = (path) => {
    setOpen(false);
    navigate(path);
  };

  const alertStyle = {
    backgroundColor: '#F3E8FF',
    color: '#6B21A8',
    padding: '8px',
    borderRadius: '8px',
    marginBottom: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontFamily: "'Roboto', sans-serif",
    animation: 'slideInFromRight 0.5s ease-out',
    fontSize: '0.875rem',
  };

  const closeButtonStyle = {
    background: 'none',
    border: 'none',
    fontSize: '0.875rem',
    cursor: 'pointer',
    color: '#6B21A8',
    fontFamily: "'Roboto', sans-serif",
  };

  const profileImageStyle = {
    borderRadius: '50%',
    marginRight: '8px',
    width: '40px',
    height: '40px',
    objectFit: 'cover',
    border: '2px solid #E5E7EB',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
  };

  return (
    <div style={{ position: 'relative', fontFamily: "'Roboto', sans-serif" }}>
      <style>{`
        @keyframes slideInFromRight {
          0% {
            transform: translateX(100%);
            opacity: 0;
          }
          100% {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>

      {error && (
        <div style={alertStyle}>
          <span>{error}</span>
          <button
            style={closeButtonStyle}
            onClick={() => setError('')}
            aria-label="Close"
          >
            ×
          </button>
        </div>
      )}

      <div
        onClick={() => setOpen(!open)}
        style={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          padding: '0.3rem',
          borderRadius: '12px',
          backgroundColor: '#ffffff',
          color: '#1f2937',
        }}
      >
        <img
          src={profileImageUrl}
          alt="Admin Profile"
          style={profileImageStyle}
          onError={handleImageError}
        />
        <div>
          <div style={{ fontWeight: '600', fontSize: '1rem', fontFamily: "'Roboto', sans-serif" }}>
            {adminUser?.username || 'Admin'}
          </div>
          <div style={{ fontSize: '12px', color: '#6B7280', fontFamily: "'Roboto', sans-serif" }}>
            Admin
          </div>
        </div>
      </div>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: '70px',
            right: 0,
            width: '220px',
            background: '#fff',
            boxShadow: '0 0 20px rgba(0, 0, 0, 0.08)',
            borderRadius: '16px',
            overflow: 'hidden',
            zIndex: 1000,
          }}
        >
          <div style={{ padding: '10px', borderBottom: '1px solid #ccc' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <img
                src={profileImageUrl}
                alt="Admin Profile"
                style={profileImageStyle}
                onError={handleImageError}
              />
              <div>
                <div style={{ fontWeight: '600', fontSize: '1rem', fontFamily: "'Roboto', sans-serif" }}>
                  {adminUser?.username || 'Admin'}
                </div>
                <div style={{ fontSize: '12px', color: '#6B7280', fontFamily: "'Roboto', sans-serif" }}>
                  {adminUser?.collegeName || 'Admin Panel'}
                </div>
              </div>
            </div>
          </div>

          <div style={{ padding: '10px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                padding: '0.5rem',
                borderRadius: '8px',
                transition: 'background-color 0.3s',
                fontFamily: "'Roboto', sans-serif",
              }}
              onClick={handleLogout}
              onMouseOver={(e) => (e.target.style.backgroundColor = '#f9fafb')}
              onMouseOut={(e) => (e.target.style.backgroundColor = 'transparent')}
            >
              <FaSignOutAlt style={{ marginRight: '8px', color: '#6B21A8' }} /> Log out
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProfileMenu;